import { css } from 'aphrodite';
import { parseAsStringEnum, useQueryState } from 'nuqs';
import * as React from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../web/components-v2/Tabs';
import { Toggle } from '../../../web/components/Toggle';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { SettingsIcon } from '../../../web/components/svgs/icons/SettingsIcon';
import { TargetGoalsIcon } from '../../../web/components/svgs/icons/TargetGoalsIcon';
import { XIconNew } from '../../../web/components/svgs/icons/XIcon';
import { CallDetailsDrawerContent } from '../../components/CallRecordingDetails/CallDetailsDrawerContent';
import { RepCoachingGoalsContent } from '../../components/RepCoachingGoalsContent';
import { useLeadServedFilterInfiniteQuery } from '../../../queries/LeadServed/useLeadServedFilterInfiniteQuery';
import { useLeadServedExportMutation } from '../../../queries/LeadServed/useLeadServedExportMutation';
import { getDisplayName } from '../../../models/NameUtils';
import { useUserQuery } from '../../../queries/User/useUserQuery';
import { useLocalStorageState } from '../../../hooks/useLocalStorage';
import type { RecordingPlaybackSurface } from '../../services/recordingPlayback';
import { useIsCoffeeMobile } from '../../shell/CoffeeMobileRouteGuard';
import { ActiveFilterChips } from './components/ActiveFilterChips';
import { CallLogList } from './components/CallLogList';
import { CallLogToolbar } from './components/CallLogToolbar';
import { IncomingCallList } from './components/IncomingCallList';
import { styleSheet } from './styles';
import { useCallDetailsRoute } from './useCallDetailsRoute';
import { useCallLogFilters } from './useCallLogFilters';

export const COFFEE_DRAWER_VALUE_SDR_GOALS = 'coffee-sdr-goals';
export const COFFEE_DRAWER_VALUE_CALL_DETAILS = 'coffee-call-details';
export const COFFEE_DRAWER_VALUE_CALL_LOG_SETTINGS = 'coffee-call-log-settings';
export const CONDENSED_FILTERS_STORAGE_KEY = 'coffee-call-log:condensed-filters';

const TABS = ['logs', 'incoming'] as const;
type CallLogTab = (typeof TABS)[number];

const PanelCloseRow: React.FC<{ onClose: () => void }> = ({ onClose }) => (
	<div className={css(styleSheet.overlayHeader)}>
		<Button type='button' variant='ghost' size='icon' aria-label='Close' onClick={onClose}>
			<XIconNew width={16} height={16} />
		</Button>
	</div>
);

export const CallLogContainer: React.FC = () => {
	const [tab, setTab] = useQueryState(
		'tab',
		parseAsStringEnum<CallLogTab>([...TABS])
			.withDefault('logs')
			.withOptions({ history: 'push' })
	);
	const { params, filter, isDefault, setFilter, removeFilter, clearAll, resetSecondary } = useCallLogFilters();
	const { callId, lastCallId, autoplay, playRequest, openCall, closeCall } = useCallDetailsRoute();
	const exportMutation = useLeadServedExportMutation();

	// '' means closed: the provider falls back to the last panel saved in localStorage for any nullish value.
	const [panel, setPanel] = React.useState('');
	const [sdrGoals, setSdrGoals] = React.useState<{ userId: string; userName: string } | null>(null);
	const [callSurface, setCallSurface] = React.useState<RecordingPlaybackSurface>('callLogDrawer');
	const isMobile = useIsCoffeeMobile();
	const [condensedFiltersSetting, setCondensedFilters] = useLocalStorageState(CONDENSED_FILTERS_STORAGE_KEY, false);
	// Phones have no right action bar to reach the setting, and the full filter set would bury the list.
	const condensedFilters = condensedFiltersSetting || isMobile;
	const managerQuery = useUserQuery({ enabled: !!params.manager, id: params.manager ?? '' });
	const managerName = managerQuery.data ? getDisplayName(managerQuery.data) : undefined;
	const callsQuery = useLeadServedFilterInfiniteQuery({ enabled: tab === 'logs', filter });
	const hasCalls = !!callsQuery.data?.pages.some(page => page.values?.length);

	const drawerValue = callId ? COFFEE_DRAWER_VALUE_CALL_DETAILS : panel;

	const onDrawerValueChange = (next: string) => {
		if (next === COFFEE_DRAWER_VALUE_CALL_DETAILS && !callId && lastCallId) {
			openCall(lastCallId);
			return;
		}
		if (callId && next !== COFFEE_DRAWER_VALUE_CALL_DETAILS) {
			closeCall({ keepLastCall: next !== '' });
		}
		setPanel(next);
	};

	const openSdrGoals = (userId: string, userName: string) => {
		if (callId) {
			closeCall({ keepLastCall: true });
		}
		setSdrGoals({ userId, userName });
		setPanel(COFFEE_DRAWER_VALUE_SDR_GOALS);
	};

	const closeSdrGoals = () => {
		setSdrGoals(null);
		setPanel('');
	};

	const openCallFrom = (surface: RecordingPlaybackSurface) => (id: string, options?: { autoplay?: boolean }) => {
		setCallSurface(surface);
		openCall(id, options);
	};

	return (
		<CoffeeDrawerProvider value={drawerValue} onValueChange={onDrawerValueChange}>
			<div className={css(styleSheet.root)}>
				<div className={css(styleSheet.pageColumn)}>
					<div className={css(styleSheet.page)}>
						<div className={css(styleSheet.content)}>
							<h1 className={css(styleSheet.title)}>Call log</h1>
							<TabRoot
								value={tab}
								onValueChange={value => setTab(value as CallLogTab)}
								className={css(styleSheet.tabs)}
							>
								<TabList>
									<TabTrigger value='logs'>Call logs</TabTrigger>
									<TabTrigger value='incoming'>Incoming</TabTrigger>
								</TabList>
								<TabContent
									value='logs'
									className={css(styleSheet.tabContent, tab !== 'logs' && styleSheet.inactiveTab)}
								>
									<CallLogToolbar
										filters={params}
										condensed={condensedFilters}
										showExport={hasCalls}
										showResetFilters={!isDefault}
										isExporting={exportMutation.isPending}
										onChange={setFilter}
										onResetSecondary={resetSecondary}
										onResetFilters={clearAll}
										onExport={() => exportMutation.mutate(filter)}
									/>
									{condensedFilters ? (
										<ActiveFilterChips filters={params} managerName={managerName} onRemove={removeFilter} />
									) : null}
									<CallLogList
										filter={filter}
										selectedCallId={callId}
										onOpenCall={openCallFrom('callLogDrawer')}
										onOpenSdrGoals={openSdrGoals}
									/>
								</TabContent>
								<TabContent
									value='incoming'
									className={css(styleSheet.tabContent, tab !== 'incoming' && styleSheet.inactiveTab)}
								>
									<IncomingCallList selectedCallId={callId} onOpenCall={openCallFrom('callLogDetails')} />
								</TabContent>
							</TabRoot>
						</div>
					</div>
				</div>
				<SideDrawer styles={[styleSheet.drawer]}>
					<AppDrawerContent value={COFFEE_DRAWER_VALUE_SDR_GOALS} unmountOnExit styles={[styleSheet.drawerContent]}>
						{sdrGoals ? (
							<>
								<PanelCloseRow onClose={closeSdrGoals} />
								<RepCoachingGoalsContent
									userId={sdrGoals.userId}
									userName={sdrGoals.userName}
									onClose={closeSdrGoals}
								/>
							</>
						) : null}
					</AppDrawerContent>
					<AppDrawerContent value={COFFEE_DRAWER_VALUE_CALL_DETAILS} unmountOnExit styles={[styleSheet.drawerContent]}>
						{callId ? (
							<>
								<PanelCloseRow
									onClose={() => {
										closeCall();
										setPanel('');
									}}
								/>
								<CallDetailsDrawerContent
									// The audio element ignores `autoPlay` once loaded, so Play on the open call remounts it.
									key={`${callId}:${playRequest}`}
									recentCallId={callId}
									autoplay={autoplay}
									isMobile={isMobile}
									surface={callSurface}
								/>
							</>
						) : null}
					</AppDrawerContent>
					<AppDrawerContent
						value={COFFEE_DRAWER_VALUE_CALL_LOG_SETTINGS}
						unmountOnExit
						styles={[styleSheet.drawerContent]}
					>
						<div className={css(styleSheet.settingsPanel)}>
							<h3 className={css(styleSheet.settingsPanelTitle)}>Filters</h3>
							<Toggle
								id='call-log-condensed-filters-toggle'
								isOn={condensedFiltersSetting}
								text='Condense filters into a "More filters" menu'
								onToggleCheckChanged={setCondensedFilters}
							/>
						</div>
					</AppDrawerContent>
					<div className={css(styleSheet.toolbar)}>
						<CoffeeDrawerToolbar>
							<AppDrawerToolbarButton
								value={COFFEE_DRAWER_VALUE_CALL_DETAILS}
								tooltip='Call details'
								aria-label='Call details'
								disabled={!callId && !lastCallId}
							>
								<PhoneIcon />
							</AppDrawerToolbarButton>
							<AppDrawerToolbarButton
								value={COFFEE_DRAWER_VALUE_SDR_GOALS}
								tooltip='SDR coaching goals'
								aria-label='SDR coaching goals'
								disabled={!sdrGoals}
							>
								<TargetGoalsIcon />
							</AppDrawerToolbarButton>
							<AppDrawerToolbarButton
								value={COFFEE_DRAWER_VALUE_CALL_LOG_SETTINGS}
								tooltip='Settings'
								aria-label='Settings'
							>
								<SettingsIcon stroke='currentColor' />
							</AppDrawerToolbarButton>
						</CoffeeDrawerToolbar>
					</div>
				</SideDrawer>
			</div>
		</CoffeeDrawerProvider>
	);
};
